import React, { useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ShoppingBag, Heart, User, Menu, X, Search, Truck } from 'lucide-react';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import { REALM_ORDER, THEMES } from '../../lib/themes';

const SiteHeader = ({ onOpenAuth }) => {
  const { count, toggleDrawer } = useCart();
  const { isAuthenticated } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();

  const navItem = ({ isActive }) =>
    [
      'relative h-full flex items-center px-4 text-[11px] font-black uppercase tracking-[0.18em]',
      'transition-colors',
      isActive ? 'text-black' : 'text-black/60 hover:text-black',
    ].join(' ');

  return (
    <header className="sticky top-0 z-40 bg-white border-b-4 border-black">
      <div className="flex items-stretch h-14 md:h-16">
        {/* LEFT: mobile menu + brand mark */}
        <button
          onClick={() => setMobileOpen(true)}
          className="md:hidden px-4 border-r-4 border-black hover:bg-black hover:text-white transition-colors"
          aria-label="Open menu"
        >
          <Menu size={20} strokeWidth={3} />
        </button>

        <Link
          to="/"
          className="flex items-center gap-2 md:gap-3 px-3 md:px-5 border-r-4 border-black bg-black hover:bg-zinc-900 transition-colors group"
          aria-label="OWLFIT — home"
        >
          <img
            src="/owlfit-mark.png"
            alt=""
            className="h-8 md:h-10 w-auto object-contain group-hover:rotate-[8deg] transition-transform"
            draggable="false"
          />
          <span className="hidden sm:block text-white font-black tracking-tighter text-xl md:text-2xl italic">
            OWLFIT
          </span>
        </Link>

        {/* CENTER: primary nav (desktop) */}
        <nav className="hidden md:flex items-stretch flex-grow">
          {REALM_ORDER.map((id) => {
            const theme = THEMES[id];
            const inRealm = pathname === `/${id}` || pathname.startsWith(`/${id}/`);
            return (
              <NavLink
                key={id}
                to={`/${id}`}
                className={({ isActive }) =>
                  [
                    navItem({ isActive: isActive || inRealm }),
                    'border-r-2 border-black',
                    inRealm ? `${theme.colors.bg} ${theme.colors.text}` : 'hover:bg-yellow-300',
                  ].join(' ')
                }
              >
                {theme.label.replace(' VENTURE', '')}
              </NavLink>
            );
          })}

          <span className="flex-grow border-r-2 border-black" />

          <NavLink
            to="/drops"
            className={({ isActive }) =>
              `${navItem({ isActive })} border-r-2 border-black hover:bg-yellow-300`
            }
          >
            Drops
          </NavLink>
          <NavLink
            to="/lookbook"
            className={({ isActive }) =>
              `${navItem({ isActive })} border-r-2 border-black hover:bg-yellow-300`
            }
          >
            Lookbook
          </NavLink>
          <NavLink
            to="/blog"
            className={({ isActive }) =>
              `${navItem({ isActive })} hover:bg-yellow-300`
            }
          >
            Journal
          </NavLink>
        </nav>

        {/* spacer for mobile so utilities push to right */}
        <div className="flex-grow md:hidden" />

        {/* RIGHT: utilities — icon-only, heavy borders */}
        <div className="flex items-stretch border-l-4 border-black bg-white">
          <Link
            to="/search"
            className="px-3 md:px-4 hover:bg-yellow-300 transition-colors flex items-center border-r-2 border-black"
            aria-label="Search"
          >
            <Search size={18} strokeWidth={2.5} />
          </Link>
          <Link
            to="/order/track"
            className="hidden md:flex px-4 hover:bg-yellow-300 transition-colors items-center border-r-2 border-black"
            aria-label="Track order"
            title="Track order"
          >
            <Truck size={18} strokeWidth={2.5} />
          </Link>
          <Link
            to={isAuthenticated ? '/account/wishlist' : '#'}
            onClick={(e) => {
              if (!isAuthenticated) {
                e.preventDefault();
                onOpenAuth?.();
              }
            }}
            className="hidden md:flex px-4 hover:bg-red-400 hover:text-white transition-colors items-center border-r-2 border-black"
            aria-label="Wishlist"
          >
            <Heart size={18} strokeWidth={2.5} />
          </Link>
          <button
            onClick={() => (isAuthenticated ? (window.location.href = '/account') : onOpenAuth?.())}
            className="hidden md:flex px-4 hover:bg-yellow-300 transition-colors items-center border-r-2 border-black"
            aria-label="Account"
          >
            <User size={18} strokeWidth={2.5} />
          </button>
          <button
            onClick={toggleDrawer}
            className="px-4 md:px-5 bg-black text-white hover:bg-yellow-400 hover:text-black transition-colors flex items-center gap-2 relative"
            aria-label={`Cart (${count} items)`}
          >
            <ShoppingBag size={18} strokeWidth={2.5} />
            <span className="font-black text-sm">{count}</span>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[60] flex md:hidden">
          <div className="absolute inset-0 bg-black/70" onClick={() => setMobileOpen(false)} />
          <div className="relative w-72 bg-white border-r-4 border-black flex flex-col animate-slide-in-right">
            <div className="bg-black text-white p-4 flex justify-between items-center border-b-4 border-yellow-400">
              <div className="flex items-center gap-3">
                <img src="/owlfit-mark.png" alt="" className="h-10 w-auto object-contain" draggable="false" />
                <span className="font-black text-2xl tracking-tighter italic">OWLFIT</span>
              </div>
              <button onClick={() => setMobileOpen(false)} className="p-1 hover:text-yellow-400" aria-label="Close menu">
                <X size={24} />
              </button>
            </div>
            <nav className="flex flex-col p-4 gap-2 text-sm uppercase font-black overflow-y-auto">
              <div className="text-[10px] tracking-widest text-gray-500 mt-1 mb-1">REALMS</div>
              {REALM_ORDER.map((id) => (
                <Link
                  key={id}
                  to={`/${id}`}
                  onClick={() => setMobileOpen(false)}
                  className={`p-3 border-2 border-black ${THEMES[id].colors.bg} ${THEMES[id].colors.text} hover:shadow-[4px_4px_0px_black]`}
                >
                  {THEMES[id].label.replace(' VENTURE', '')}
                </Link>
              ))}
              <div className="text-[10px] tracking-widest text-gray-500 mt-3 mb-1">SHOP</div>
              <Link to="/drops" onClick={() => setMobileOpen(false)} className="p-3 border-2 border-black hover:bg-yellow-300">
                Drops
              </Link>
              <Link to="/lookbook" onClick={() => setMobileOpen(false)} className="p-3 border-2 border-black hover:bg-yellow-300">
                Lookbook
              </Link>
              <Link to="/blog" onClick={() => setMobileOpen(false)} className="p-3 border-2 border-black hover:bg-yellow-300">
                Journal
              </Link>

              <div className="text-[10px] tracking-widest text-gray-500 mt-3 mb-1">HELP</div>
              <Link to="/order/track" onClick={() => setMobileOpen(false)} className="p-3 border-2 border-black hover:bg-yellow-300">
                Track order
              </Link>
              <Link to="/size-guide" onClick={() => setMobileOpen(false)} className="p-3 border-2 border-black hover:bg-yellow-300">
                Size guide
              </Link>
              <Link to="/returns" onClick={() => setMobileOpen(false)} className="p-3 border-2 border-black hover:bg-yellow-300">
                Returns
              </Link>

              <div className="h-px bg-gray-200 my-2" />
              {isAuthenticated ? (
                <Link to="/account" onClick={() => setMobileOpen(false)} className="p-3 border-2 border-black bg-black text-white">
                  Account
                </Link>
              ) : (
                <button
                  onClick={() => {
                    setMobileOpen(false);
                    onOpenAuth?.();
                  }}
                  className="p-3 border-2 border-black bg-black text-white text-left"
                >
                  Login / Sign up
                </button>
              )}
            </nav>
          </div>
        </div>
      )}
    </header>
  );
};

export default SiteHeader;
